/**
 * Phase 20 / Task 20.7 — §80 "Saved mapping profiles": review and delete.
 *
 * A profile is saved on the mapping step (`PUT …/mapping` with `saveAsProfile`)
 * and loaded there; there is deliberately no way to create one here, because a
 * profile is only worth keeping as "the mapping I just got working against a
 * real file" (the API route's header says the same). Profiles are shared across
 * the tenant (§80), so deleting one removes it for everyone, and the
 * confirmation says so.
 */

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiOpts } from '@lodex-pro/web-shared';

import { useUserTimezone } from '../../lib/use-user-timezone';
import { deleteProfile, listProfiles } from './api';
import { formatWhen } from './HistoryPanel';
import { familyLabel } from './mapping-state';
import type { MappingProfile } from './types';
import { ActionButton, Notice, Panel, muted } from './ui';
import { formatNumber } from '@lodex-pro/shared/number-format';

const PROFILES_KEY = ['import-profiles'] as const;

function mappedColumns(p: MappingProfile): number {
  return Object.values(p.mapping).filter((v) => v !== null).length;
}

export function ProfilesPanel(): React.JSX.Element {
  const apiOpts = useApiOpts();
  const tz = useUserTimezone();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: PROFILES_KEY,
    refetchOnMount: 'always',
    queryFn: () => listProfiles(apiOpts),
  });
  const remove = useMutation<void, Error, MappingProfile>({
    mutationFn: (p) => deleteProfile(apiOpts, p.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PROFILES_KEY });
      // The mapping step lists profiles per batch; drop those copies too.
      void qc.invalidateQueries({ queryKey: ['imports'] });
    },
  });

  const byFamily = new Map<string, MappingProfile[]>();
  for (const p of query.data?.profiles ?? []) {
    byFamily.set(p.entityFamily, [...(byFamily.get(p.entityFamily) ?? []), p]);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Panel labelledBy="import-profiles-heading">
        <h2 id="import-profiles-heading" style={{ fontSize: 18, fontWeight: 600 }}>
          Saved mapping profiles
        </h2>
        <p style={{ ...muted, marginTop: 8 }}>
          A profile remembers which of your columns go where, so the next file in the same layout
          maps itself. Save one on the column-mapping step. Profiles are shared with everyone in
          your company who imports.
        </p>
      </Panel>
      {query.isPending && <Panel>Loading…</Panel>}
      {query.isError && (
        <Notice tone="error" role="alert">
          {query.error.message}
        </Notice>
      )}
      {remove.isError && (
        <Notice tone="error" role="alert">
          {remove.error.message}
        </Notice>
      )}
      {query.data?.profiles.length === 0 && <Panel>No saved profiles yet.</Panel>}
      {[...byFamily.entries()].map(([family, profiles]) => (
        <Panel key={family}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{familyLabel(family)}</h3>
          <ul style={{ padding: 0, margin: '8px 0 0' }}>
            {profiles.map((p) => (
              <li
                key={p.id}
                style={{
                  listStyle: 'none',
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  padding: '8px 0',
                  borderBottom: '1px solid var(--color-border-primary)',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {p.name}
                  </div>
                  <div style={muted}>
                    {mappedColumns(p)} {mappedColumns(p) === 1 ? 'column' : 'columns'} mapped · used{' '}
                    {formatNumber(p.useCount)} {p.useCount === 1 ? 'time' : 'times'}
                    {p.lastUsedAt !== null ? `, last ${formatWhen(p.lastUsedAt, tz)}` : ''}
                    {p.createdByName !== null ? ` · saved by ${p.createdByName}` : ''}
                  </div>
                </div>
                <ActionButton
                  disabled={remove.isPending}
                  aria-label={`Delete profile ${p.name}`}
                  onClick={(): void => {
                    if (
                      !window.confirm(
                        `Delete the profile "${p.name}"? It is removed for everyone in your company.`,
                      )
                    ) {
                      return;
                    }
                    remove.mutate(p);
                  }}
                >
                  {remove.isPending && remove.variables.id === p.id ? 'Deleting…' : 'Delete'}
                </ActionButton>
              </li>
            ))}
          </ul>
        </Panel>
      ))}
    </div>
  );
}
